import React from 'react';
import styled from 'styled-components';
import Moment from 'moment';
import Util from '../../../forecast-app/shared/util/util';
import {PERIOD_BUDGET_TYPE} from '../../../constants';
import {CSS_CONSTANTS} from '../../../css_variables';
import LockedIcon from '../../../images/integrations/locked_icon';
import {remapId} from '../../../forecast-app/shared/util/FinancialInternationalisationUtil';

const PeriodHeaderWrapper = styled.div`
	min-width: ${props => props.fixedWidth};
	display: flex;
	margin: 8px 0;
	padding: 0 66px;
	--project-color: ${props => props.projectColor};
	flex-grow: 1;
	background: repeating-linear-gradient(to right, var(--project-color, #a1a1a1) 0px 9px, transparent 3px 22px) right
		center/100% 2px border-box;
	background-repeat: no-repeat;
`;

const PeriodHeaderElem = styled.div`
	display: flex;
	font-size: 14px;
	min-width: ${props => props.fixedWidth};
	max-width: ${props => props.fixedWidth};

	.period-name {
		max-width: 200px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: ${CSS_CONSTANTS.v2_text_gray};
	}
`;

const HeaderTitle = styled.div`
	font-size: 9px;
	text-transform: uppercase;
	color: ${CSS_CONSTANTS.v2_text_light_gray};
	margin-right: 9px;
	align-self: center;
`;

const HeaderValue = styled.div``;

const TitleValueWrapper = styled.div`
	display: flex;
	padding: 0 10px;
	background: white;
`;

const PhasesPeriodHeader = ({
	intl,
	intl: {formatMessage, formatNumber},
	currencySymbol,
	period,
	timeRegsForPeriod,
	phases,
	taskCellMaxWidth,
	projectColor,
	minWidth,
	isEstimatedInHours,
	isEstimatedInNewPoints,
}) => {
	if (!period) {
		return (
			<PeriodHeaderWrapper fixedWidth={minWidth + 'px'} projectColor={projectColor}>
				<PeriodHeaderElem>
					<TitleValueWrapper>{formatMessage({id: 'retainer.outside_periods'})}</TitleValueWrapper>
				</PeriodHeaderElem>
			</PeriodHeaderWrapper>
		);
	}

	const inHours = period.periodBudgetType !== PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const periodStartDate = Util.CreateMomentDate(period.startYear, period.startMonth, period.startDay);
	const periodEndDate = Util.CreateMomentDate(period.endYear, period.endMonth, period.endDay);

	const periodTarget = inHours
		? period.periodHoursAmount + (period.periodDifferenceHoursAmount || 0) + (period.sharedPeriodDifferenceHoursAmount || 0)
		: period.periodPriceAmount +
		  (period.periodDifferencePriceAmount || 0) +
		  (period.sharedPeriodDifferencePriceAmount || 0);
	const withSuffix = val => {
		return inHours
			? Util.convertMinutesToFullHour(val * 60, intl)
			: Util.getFormattedCurrencyValue(currencySymbol, formatNumber(val, {format: 'always_two_decimal'}));
	};

	const periodSpentValue = phases.reduce((total, phase) => total + (inHours ? phase.forecast / 60 : phase.price), 0);

	const periodActual = inHours
		? timeRegsForPeriod.reduce((acc, timeReg) => acc + timeReg.node.minutesRegistered / 60, 0)
		: timeRegsForPeriod.reduce((acc, timeReg) => acc + timeReg.node.price, 0);

	return (
		<PeriodHeaderWrapper fixedWidth={minWidth + 'px'} projectColor={projectColor}>
			<PeriodHeaderElem fixedWidth={taskCellMaxWidth + 26 + 'px'}>
				<TitleValueWrapper>
					{period.periodLocked ? (
						<LockedIcon
							width={12}
							height={16}
							className="status-icon"
							title={formatMessage(
								{id: 'retainer.period_locked'},
								{lockedTime: Moment(period.periodLockedTime).format('LL')}
							)}
						/>
					) : null}
					<div className="period-name" title={period.name}>
						{period.name}
					</div>
				</TitleValueWrapper>
			</PeriodHeaderElem>
			<PeriodHeaderElem fixedWidth={'250px'}>
				<TitleValueWrapper>{periodStartDate.format('ll') + ' - ' + periodEndDate.format('ll')}</TitleValueWrapper>
			</PeriodHeaderElem>
			{period.periodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS ? null : (
				<>
					<PeriodHeaderElem fixedWidth={'230px'}>
						<TitleValueWrapper>
							<HeaderTitle>
								{inHours
									? formatMessage({id: 'retainer.period_hours'})
									: formatMessage({id: 'retainer.period_price'})}
							</HeaderTitle>
							<HeaderValue>{withSuffix(periodTarget)}</HeaderValue>
						</TitleValueWrapper>
					</PeriodHeaderElem>
					{!(!isEstimatedInHours && isEstimatedInNewPoints) && (
						<PeriodHeaderElem fixedWidth={'230px'}>
							<TitleValueWrapper>
								<HeaderTitle>
									{inHours
										? formatMessage({id: 'retainer.planned_time'})
										: formatMessage({id: 'retainer.planned_price'})}
								</HeaderTitle>
								<HeaderValue>{withSuffix(periodSpentValue)}</HeaderValue>
							</TitleValueWrapper>
						</PeriodHeaderElem>
					)}
				</>
			)}
			<PeriodHeaderElem fixedWidth={'230px'}>
				<TitleValueWrapper>
					<HeaderTitle>
						{inHours
							? formatMessage({id: 'common.time_entries'})
							: formatMessage({id: remapId('project_budget.actual_revenue_no_expenses')})}
					</HeaderTitle>
					<HeaderValue>{withSuffix(periodActual)}</HeaderValue>
				</TitleValueWrapper>
			</PeriodHeaderElem>
		</PeriodHeaderWrapper>
	);
};

export default PhasesPeriodHeader;
